import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
} from 'firebase/auth';
import logger from '../../../utils/logger';
import { useAuth } from '../AuthProvider';
import styles from '../LoginEnhanced.module.css';
import { loadAuth } from './authActionFirebase';
import {
  completePasswordReset,
  completeVerifyEmailAction,
  inspectResetPasswordCode,
  mapAuthActionErrorMessage,
  validateResetPassword,
} from './authActionHelpers';

type HandlerStatus = 'verifying' | 'reset' | 'error';
type ErrorKind = 'verify' | 'reset' | 'generic';

const verifyEmailJobs = new Map<string, Promise<string>>();

export function VerifyEmailRedirect() {
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  return <Navigate to={query ? `/auth/action?${query}` : '/auth/action'} replace />;
}

export default function AuthActionHandler() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOutUser } = useAuth();
  const [status, setStatus] = useState<HandlerStatus>('verifying');
  const [message, setMessage] = useState('');
  const [errorKind, setErrorKind] = useState<ErrorKind>('generic');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  useEffect(() => {
    let cancelled = false;

    const fail = (kind: ErrorKind, code?: string) => {
      if (cancelled) return;
      setErrorKind(kind);
      setMessage(mapAuthActionErrorMessage(code, kind));
      setStatus('error');
    };

    const run = async () => {
      if (!oobCode) {
        fail(mode === 'resetPassword' ? 'reset' : mode === 'verifyEmail' ? 'verify' : 'generic');
        return;
      }

      if (mode === 'verifyEmail') {
        const existing = verifyEmailJobs.get(oobCode);
        const job = existing || (async () => {
          const auth = await loadAuth();
          logger.debug('Applying email verification action code');
          return completeVerifyEmailAction({
            oobCode,
            continueUrl,
            origin: window.location.origin,
            auth,
            checkActionCode,
            applyActionCode,
            signOutUser,
          });
        })();

        if (!existing) verifyEmailJobs.set(oobCode, job);

        try {
          const path = await job;
          if (!cancelled) navigate(path, { replace: true });
        } catch (error) {
          const code = (error as { code?: string })?.code;
          logger.error('Email verification failed:', error);
          fail('verify', code);
          verifyEmailJobs.delete(oobCode);
        }
        return;
      }

      if (mode === 'resetPassword') {
        try {
          const auth = await loadAuth();
          await inspectResetPasswordCode({
            auth,
            oobCode,
            checkActionCode,
          });
          if (cancelled) return;
          setStatus('reset');
        } catch (error) {
          const code = (error as { code?: string })?.code;
          logger.error('Password reset link check failed:', error);
          fail('reset', code);
        }
        return;
      }

      fail('generic');
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [continueUrl, mode, navigate, oobCode, signOutUser]);

  const handleResetSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validateResetPassword(password, confirmPassword);
    setPasswordError(validation.password);
    setConfirmPasswordError(validation.confirmPassword);
    if (validation.password || validation.confirmPassword || !oobCode) return;

    setResetSubmitting(true);
    setMessage('');
    try {
      const auth = await loadAuth();
      const path = await completePasswordReset({
        oobCode,
        password,
        auth,
        confirmPasswordReset,
        signOutUser,
      });
      navigate(path, { replace: true });
    } catch (error) {
      const code = (error as { code?: string })?.code;
      logger.error('Password reset failed:', error);
      setErrorKind('reset');
      setMessage(mapAuthActionErrorMessage(code, 'reset'));
      setStatus('error');
    } finally {
      setResetSubmitting(false);
    }
  };

  const title = status === 'reset'
    ? 'Set a new password'
    : status === 'error'
      ? (errorKind === 'reset' ? 'Password reset failed' : errorKind === 'verify' ? 'Verification failed' : 'Email link failed')
      : 'Confirming your email';

  return (
    <div className={styles.splitLayout} style={{ justifyContent: 'center' }}>
      <div className={styles.rightPanel}>
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>{title}</h2>

          {status === 'verifying' && (
            <p className={styles.formSubtitle}>Please wait while we confirm your email address.</p>
          )}

          {status === 'reset' && (
            <form onSubmit={handleResetSubmit} noValidate>
              <p className={styles.formSubtitle}>Choose a new password for your VerdeKai account.</p>
              {message && <div className={styles.errorMsg}>{message}</div>}
              <div className={styles.formGroup}>
                <div className={styles.floatingLabelGroup}>
                  <input
                    type="password"
                    name="password"
                    id="reset-password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className={passwordError ? styles.inputError : ''}
                    required
                    disabled={resetSubmitting}
                  />
                  <label htmlFor="reset-password" className={password ? styles.hasValue : ''}>
                    New password
                  </label>
                </div>
                {passwordError && <span className={styles.errorText}>{passwordError}</span>}
              </div>
              <div className={styles.formGroup}>
                <div className={styles.floatingLabelGroup}>
                  <input
                    type="password"
                    name="confirmPassword"
                    id="reset-confirm-password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className={confirmPasswordError ? styles.inputError : ''}
                    required
                    disabled={resetSubmitting}
                  />
                  <label htmlFor="reset-confirm-password" className={confirmPassword ? styles.hasValue : ''}>
                    Confirm password
                  </label>
                </div>
                {confirmPasswordError && <span className={styles.errorText}>{confirmPasswordError}</span>}
              </div>
              <button type="submit" className={styles.signInBtn} disabled={resetSubmitting}>
                {resetSubmitting ? 'Updating password...' : 'Update password'}
              </button>
            </form>
          )}

          {status === 'error' && (
            <div>
              <p className={styles.formSubtitle}>{message}</p>
              {errorKind === 'verify' && (
                <button
                  type="button"
                  className={styles.signInBtn}
                  onClick={() => navigate('/signup?step=2&resend=true')}
                  style={{ marginBottom: '0.875rem' }}
                >
                  Request New Verification Email
                </button>
              )}
              <button
                type="button"
                className={styles.secondaryActionBtn}
                onClick={() => navigate('/login')}
              >
                Back to Login
              </button>
            </div>
          )}

          <div className={styles.signupPrompt}>
            Need help? <a href="mailto:support@verdekai.com">Contact Support</a>
          </div>
        </div>
      </div>
    </div>
  );
}
